import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import {
  BsSearch,
  BsBoxSeam,
  BsTruck,
  BsCheckCircle,
  BsClock,
  BsXCircle,
} from "react-icons/bs";
import { FaRupeeSign } from "react-icons/fa";
import { trackOrder } from "../../config/api";
import LightLoader from "../common/LightLoader";
import { getImageUrl } from "../../utils/helpers";

const STATUS_LABELS = {
  pending: "Awaiting Payment",
  paid: "Paid",
  confirmed: "Confirmed",
  processing: "Processing",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  paid: "bg-green-100 text-green-800",
  confirmed: "bg-blue-100 text-blue-800",
  processing: "bg-blue-100 text-blue-800",
  shipped: "bg-indigo-100 text-indigo-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const OrderTrackPage = () => {
  const { token: urlToken } = useParams();
  const navigate = useNavigate();
  const [tokenInput, setTokenInput] = useState(urlToken || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(!!urlToken);
  const [error, setError] = useState(null);

  const fetchOrder = async (token) => {
    if (!token?.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await trackOrder(token.trim());
      if (result.success && result.data) {
        setOrder(result.data);
        if (token.trim() !== urlToken) {
          navigate(`/track/${token.trim()}`, { replace: true });
        }
      } else {
        setError("Order not found. Please check your tracking token.");
        setOrder(null);
      }
    } catch (err) {
      const status = err.response?.status;
      const apiMessage = err.response?.data?.message;
      if (status === 404) {
        setError(apiMessage || "Order not found. Please check your tracking token.");
      } else if (status === 400) {
        setError(apiMessage || "Invalid tracking code format.");
      } else {
        setError(
          apiMessage ||
            "Could not load order. Please check your tracking token and try again.",
        );
      }
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (urlToken) {
      setTokenInput(urlToken);
      fetchOrder(urlToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlToken]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchOrder(tokenInput);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const handleTokenChange = (value) => {
    if (value.length <= 8) {
      setTokenInput(value.toUpperCase().replace(/[^A-Z0-9]/g, ""));
    } else {
      setTokenInput(value.replace(/[^a-zA-Z0-9]/g, ""));
    }
  };

  return (
    <>
      <Helmet>
        <title>Track Order | Knots & Krafts</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <BsBoxSeam className="text-4xl text-amber-600 mx-auto mb-3" />
            <h1 className="text-3xl font-bold text-gray-900">Track Your Order</h1>
            <p className="text-gray-600 mt-2">
              Enter the 8-character tracking code from your confirmation email
            </p>
          </motion.div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-md border border-amber-100 p-6 mb-8"
          >
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tracking Code
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                value={tokenInput}
                onChange={(e) => handleTokenChange(e.target.value)}
                placeholder="e.g. A3K9X2M7"
                maxLength={64}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none text-sm font-mono tracking-widest uppercase"
              />
              <button
                type="submit"
                disabled={loading || !tokenInput.trim()}
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 disabled:opacity-50 transition-colors"
              >
                <BsSearch />
                Track
              </button>
            </div>
          </form>

          {loading && (
            <div className="flex justify-center py-12">
              <LightLoader />
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-center">
              {error}
            </div>
          )}

          {order && !loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              <div className="bg-white rounded-2xl shadow-md border border-amber-100 p-6">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-500">Order Number</p>
                    <p className="text-xl font-bold text-gray-900">
                      {order.orderNumber}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Placed {formatDate(order.createdAt)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        STATUS_COLORS[order.status] || "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {STATUS_LABELS[order.status] || order.status}
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        order.paymentStatus === "completed"
                          ? "bg-green-100 text-green-800"
                          : order.paymentStatus === "failed"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      Payment: {order.paymentStatus}
                    </span>
                  </div>
                </div>

                {order.trackingNumber && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6 flex items-start gap-3">
                    <BsTruck className="text-indigo-600 text-xl mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-indigo-900">Shipment Tracking</p>
                      {order.shippingCompany && (
                        <p className="text-sm text-indigo-700">
                          Carrier: {order.shippingCompany}
                        </p>
                      )}
                      <p className="text-sm text-indigo-700">
                        Tracking #: {order.trackingNumber}
                      </p>
                      {order.trackingUrl && (
                        <a
                          href={order.trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-indigo-600 underline mt-1 inline-block"
                        >
                          Track shipment →
                        </a>
                      )}
                    </div>
                  </div>
                )}

                <h3 className="font-semibold text-gray-900 mb-3">Items</h3>
                <div className="space-y-3 mb-6">
                  {order.items?.map((item) => (
                    <div
                      key={item.id}
                      className="flex gap-4 items-center border border-gray-100 rounded-xl p-3"
                    >
                      {item.image && (
                        <img
                          src={getImageUrl(item.image)}
                          alt={item.name}
                          className="w-14 h-14 object-cover rounded-lg"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {item.name}
                        </p>
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-semibold text-gray-900 flex items-center">
                        <FaRupeeSign className="text-xs mr-0.5" />
                        {(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-100 pt-4 space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span className="flex items-center">
                      <FaRupeeSign className="text-xs" />
                      {Number(order.itemsPrice).toFixed(2)}
                    </span>
                  </div>
                  {Number(order.discountAmount) > 0 && (
                    <div className="flex justify-between text-green-700">
                      <span>
                        Discount
                        {order.couponCode ? ` (${order.couponCode})` : ""}
                      </span>
                      <span className="flex items-center">
                        -<FaRupeeSign className="text-xs" />
                        {Number(order.discountAmount).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span className="flex items-center">
                      <FaRupeeSign className="text-xs" />
                      {Number(order.shippingPrice).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between font-bold text-gray-900 text-base pt-2 border-t">
                    <span>Total</span>
                    <span className="flex items-center">
                      <FaRupeeSign className="text-xs" />
                      {Number(order.totalPrice).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {order.timeline?.length > 0 && (
                <div className="bg-white rounded-2xl shadow-md border border-amber-100 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Order Timeline</h3>
                  <div className="space-y-4">
                    {order.timeline.map((entry, idx) => (
                      <div key={idx} className="flex gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {entry.new_status === "delivered" ? (
                            <BsCheckCircle className="text-green-500" />
                          ) : entry.new_status === "cancelled" ? (
                            <BsXCircle className="text-red-500" />
                          ) : (
                            <BsClock className="text-amber-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 capitalize">
                            {(entry.new_status || "").replace(/_/g, " ")}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDate(entry.changed_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-center">
                <Link
                  to="/shop"
                  className="text-amber-700 hover:text-amber-800 font-medium text-sm"
                >
                  ← Continue Shopping
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderTrackPage;
